/**
 * The camera: where the globe sits on screen and how it is turned, for the
 * intro's last frame, the hero, each place, the global stop and the outro, and
 * the flight between them. The scroll position picks a point between two
 * keyframes; the camera rests on each stop while the reader reads it.
 * The window's size comes in as a `Viewport` value on every call.
 */
import { RAD } from './data/geo'
import { clamp, lerp, lerpAng, smootherstep, smoothstep, wrapAng } from './easing'
import { longitudeAt, radiusAt, TILT } from './globe-look'
import { shellAt, T_DET } from './intro/supernova'
import type { View } from './renderer/renderer'
import type { Viewport } from './viewport'

/* =====================================================================
   TUNING — lengths are in screen widths (× width) or heights (× height) as noted.
   The globe's own size and turn are in globe-look.ts.
   ===================================================================== */
export const FAR_D = 400 /* camera distance for the whole globe: the intro's own (almost no perspective) */
const NEAR_D = 1.9 /* camera distance at a place, in globe radii (smaller = lower, more horizon) */
const NEAR_R = 0.95 /* the globe's silhouette at a place, × height */
const PLACE_ABOVE = 22 /* the place sits this far (deg) up from the globe's centre, towards the horizon */
const PLACE_Y = 0.43 /* ...and this high on screen (× height) */
export const HERO_X = 0.29 /* the hero: the globe's centre (× width) ... */
export const HERO_R = 0.4 /* ...and its radius (× height) */
const SIDE_X = 0.3 /* at a place the globe's centre sits this far in from the edge (× width) */
const GLOBAL_X = HERO_X /* the global stop: globe centre from its edge (× width) */
const DRIFT = 2.2 /* deg: the globe keeps turning a touch while you rest on a place */
const HOLD = 0.34 /* share of each stretch the camera rests on a place while you read */
const HOP = 0.55 /* how far the camera pulls back mid-flight between two places */
const FLY_DUR = [1.3, 2.6] /* s: a short turn ... the far side of the world */
const FLY_JUMP = 0.38 /* s into a flight when the page is moved, the copy being out of sight */
const FADE_SPAN = 0.85 /* services: the globe sinks into the dark as the section rises, over this much of the screen */
const NAV_TURN = 0.85 /* hovering a place turns the globe this far towards it (0 = not at all) */
const NAV_EASE = 3.2 /* how briskly the globe turns to a hovered place (a spring: higher = snappier) */
/* ===================================================================== */

/** Globe screen radius for the far views on desktop. */
export function farRs(vp: Viewport): number {
  return Math.min(vp.height * HERO_R, vp.width * 0.25)
}

/** Globe screen radius for the far views on mobile (hero and outro). */
export function mobileRs(vp: Viewport): number {
  return Math.min(vp.width * 0.42, vp.height * 0.26)
}

/** What the camera needs to know of a stop. */
export interface CameraStop {
  readonly el: { offsetTop: number; offsetHeight: number }
  readonly copySide: 'left' | 'right' | undefined
  readonly global: boolean
  readonly lat: number
  readonly lon: number
  /** How present the stop is, 0..1. Written by the camera as the page scrolls. */
  act: number
}

/** A keyframe on the scroll. The places and the global stop carry their stop; the hero and the outro have none. */
type Key =
  | { readonly s: number; readonly kind: 'hero' | 'outro' }
  | { readonly s: number; readonly kind: 'place' | 'global'; readonly stop: CameraStop }

interface Flight {
  readonly t0: number
  readonly s0: number
  readonly s1: number
  readonly y: number
  readonly v0: View
  /** Written by the camera once the copy is out of sight and the page has been moved under it. */
  jumped: boolean
  /** Written by the camera each frame: the turn so far, which follows a destination that is still turning. */
  lamTurn: number
  readonly dur: number
}

/** What one frame of the camera comes to. */
export interface FrameView {
  readonly v: View
  /** How far the globe has sunk into the dark, 0..1. */
  readonly gone: number
  /** During a flight, the scroll position (in screen heights) to carry on from; else null. */
  readonly s: number | null
  /** Set once, when the page should be scrolled to this y (px). */
  readonly jumpTo: number | null
  /** Progress of the flight, 0..1, or null when none is under way. */
  readonly flightU: number | null
}

/** What the camera needs to know of the page layout. */
export interface CameraOptions {
  /** The stops in page order; the camera reads `el.offsetTop` and `el.offsetHeight` from each. */
  readonly stops: CameraStop[]
  /** The services section; the camera reads `offsetTop` only. */
  readonly servicesEl: { offsetTop: number }
}

/**
 * Interpolate between views `A` and `B` at way `u` (0..1). Position,
 * radius and tilt move linearly. The camera distance `d` moves
 * geometrically in `d − 1`, so a zoom from far to near feels even.
 * The turn `lam` takes the short way round unless `lamTurn` is given.
 *
 * `lamTurn` is the whole turn from `A` to `B` in radians, added to `A.lam`
 * (so `B.lam` is ignored). Its sign gives the direction; it may exceed
 * π to go the long way round.
 *
 * Pure: does not modify `A` or `B`.
 */
export function mixView(A: View, B: View, u: number, lamTurn?: number): View {
  return {
    cx: lerp(A.cx, B.cx, u),
    cy: lerp(A.cy, B.cy, u),
    Rs: lerp(A.Rs, B.Rs, u),
    d: 1 + Math.exp(lerp(Math.log(A.d - 1), Math.log(B.d - 1), u)),
    lam: lamTurn === undefined ? lerpAng(A.lam, B.lam, u) : A.lam + lamTurn * u,
    tilt: lerp(A.tilt, B.tilt, u),
  }
}

/** A critically damped spring: eases in and out, and a change of mind mid-way never jolts. */
function spring(o: { x: number; v: number }, to: number, dt: number, w: number): void {
  for (let n = Math.ceil(dt / 0.008), h = dt / n; n > 0; n--) {
    o.v += (w * w * (to - o.x) - 2 * w * o.v) * h
    o.x += o.v * h
  }
}

/**
 * Create the camera for the page. Writes `act` on each stop as the
 * page scrolls.
 *
 * Call `measureKeys` before asking for any scroll or frame view, and
 * again whenever the window size or page layout changes. A flight
 * (via `flyTo`) needs a previous frame first (`hasView` must be true).
 */
export function createCamera({ stops, servicesEl }: CameraOptions) {
  let keys: Key[] = []
  let lastV: View | null = null
  let flight: Flight | null = null
  /** For each stretch of the scroll, the turn chosen when the move began, kept until it comes to rest. */
  const segmentTurns: number[] = []
  const hTurn = { x: 0, v: 0 }
  const hTilt = { x: 0, v: 0 }
  let hoverAim: number | null = null
  /** How present the hero, the global stop (the network), the backlight away and the shade are. */
  const weights = { hero: 1, net: 0, away: 0, shade: 0 }

  return {
    weights,
    /** The flight under way, if any. */
    get flying() {
      return flight !== null
    },
    /** True once a frame has been worked out, so a flight has somewhere to start from. */
    get hasView() {
      return lastV !== null
    },
    /** Where each keyframe sits on the scroll, in screen heights. Measure again when the window or the page changes. */
    measureKeys(vp: Viewport) {
      const H = vp.height
      keys = [{ s: 0, kind: 'hero' }]
      for (const st of stops) {
        keys.push({
          s: (st.el.offsetTop + st.el.offsetHeight / 2 - H / 2) / H,
          kind: st.global ? 'global' : 'place',
          stop: st,
        })
      }
      keys.push({ s: (servicesEl.offsetTop - H * 0.35) / H, kind: 'outro' })
    },
    introView,
    scrollView,
    goneAt,
    /** Hovering a place turns the globe towards it. */
    hoverTurn(t: number, dt: number, stop: CameraStop | undefined) {
      let toL = 0
      let toT = 0
      if (stop && !stop.global) {
        /* the full turn to the place, kept continuous from the last aim, so it never flips to the other way round */
        const prev = hoverAim !== null ? hoverAim : hTurn.x / NAV_TURN
        hoverAim = prev + wrapAng(stop.lon * RAD - longitudeAt(t - T_DET) - prev)
        toL = hoverAim * NAV_TURN
        toT = (clamp(stop.lat, -35, 45) * RAD - TILT) * NAV_TURN * 0.6
      } else hoverAim = null
      spring(hTurn, toL, dt, NAV_EASE)
      spring(hTilt, toT, dt, NAV_EASE)
    },
    /** Begin a flight to scroll position `s1` (page y `y`); false when it is already there. */
    flyTo(vp: Viewport, y: number, s1: number, sS: number, t: number): boolean {
      if (Math.abs(s1 - (flight ? flight.s1 : sS)) < 0.05) return false
      const v0 = lastV!
      const v1 = scrollView(vp, s1, t)
      const lamTurn = wrapAng(v1.lam - v0.lam)
      const turn = Math.abs(lamTurn) + Math.abs(v1.tilt - v0.tilt)
      flight = {
        t0: t,
        s0: flight ? flight.s1 : sS,
        s1,
        y,
        v0,
        jumped: false,
        lamTurn,
        dur: lerp(FLY_DUR[0], FLY_DUR[1], clamp(turn / Math.PI, 0, 1)),
      }
      return true
    },
    /** The camera for one frame: the scroll's view, or a flight's. `sS` is the smoothed, `sT` the true scroll. */
    frameView(vp: Viewport, t: number, sS: number, sT: number): FrameView {
      let v: View
      let gone: number
      let s: number | null = null
      let jumpTo: number | null = null
      let flightU: number | null = null
      const f = flight
      if (f) {
        const fu = clamp((t - f.t0) / f.dur, 0, 1)
        const fm = smootherstep(0.3, 0.7, fu)
        /* the copy is out of sight: move the page under it */
        if (!f.jumped && t - f.t0 > FLY_JUMP) {
          jumpTo = f.y
          f.jumped = true
        }
        /* the places, the network and the dots fade from where we were to where we go */
        scrollView(vp, f.s0, t)
        const a0 = stops.map((q) => q.act)
        const n0 = weights.net
        const h0 = weights.hero
        const aw0 = weights.away
        const sh0 = weights.shade
        const vT = scrollView(vp, f.s1, t)
        stops.forEach((q, j) => (q.act = lerp(a0[j], q.act, fm)))
        weights.net = lerp(n0, weights.net, fm)
        weights.hero = lerp(h0, weights.hero, fm)
        weights.away = lerp(aw0, weights.away, smootherstep(0, 1, fu))
        weights.shade = lerp(sh0, weights.shade, smootherstep(0, 1, fu))
        gone = lerp(goneAt(vp, f.s0), goneAt(vp, f.s1), fm)
        v = flyView(f, f.v0, vT, fu)
        s = f.s1
        flightU = fu
        if (fu >= 1) flight = null
      } else {
        v = scrollView(vp, sS, t)
        gone = goneAt(vp, sT) /* the dark keeps pace with the section itself */
      }
      lastV = { ...v }
      return { v, gone, s, jumpTo, flightU }
    },
  }

  /** The arc of lift added mid-hop between two close-up views. */
  function hopLift(d: number, e: number): number {
    return HOP * Math.sin(Math.PI * e) * (d - 0.6)
  }

  /** The flight: the same move as scrolling from one place to the next, lifting a little between two close-ups. */
  function flyView(f: Flight, A: View, B: View, u: number): View {
    const e = smootherstep(0, 1, u)
    f.lamTurn += wrapAng(
      B.lam - A.lam - f.lamTurn
    ) /* B may be turning: follow it, never flip to the other way round */
    const v = mixView(A, B, e, f.lamTurn)
    if (A.d < 5 && B.d < 5) v.d += hopLift(v.d, e)
    return v
  }

  /** How far the globe has sunk into the dark, with the page scrolled to `s`. */
  function goneAt(vp: Viewport, s: number): number {
    const H = vp.height
    return smoothstep(0, 1, (H - (servicesEl.offsetTop - s * H)) / (H * FADE_SPAN))
  }

  /** The camera for a (smoothed) scroll position, `s` in screen heights. */
  function scrollView(vp: Viewport, s: number, t: number): View {
    let k = 0
    while (k < keys.length - 2 && s > keys[k + 1].s) k++
    const A = keys[k]
    const B = keys[k + 1]
    const u = clamp((s - A.s) / (B.s - A.s), 0, 1)
    const h = HOLD / 2
    const e = smootherstep(h, 1 - h, u)
    const VA = keyView(vp, A, t)
    const VB = keyView(vp, B, t)
    /* the whole-Earth stops keep turning, so the short way round to the next place can
       swap sides mid-move: keep the way we started, and choose afresh only at rest */
    let lamTurn = wrapAng(VB.lam - VA.lam)
    const kept = segmentTurns[k]
    if (e > 0.001 && e < 0.999 && kept !== undefined) lamTurn = kept + wrapAng(lamTurn - kept)
    segmentTurns[k] = lamTurn
    const v = mixView(VA, VB, e, lamTurn)
    weights.away = lerp(keyAway(A), keyAway(B), e)
    weights.shade = lerp(keyShade(A), keyShade(B), e)
    /* between two places the camera lifts a little, as if flying over */
    if (A.kind === 'place' && B.kind === 'place') v.d += hopLift(v.d, e)
    /* how present each stop is */
    weights.net = 0
    for (let i = 0; i < stops.length; i++) {
      const K = keys[i + 1]
      const gap = Math.max(Math.min(K.s - keys[i].s, keys[i + 2].s - K.s), 0.5)
      stops[i].act = 1 - smoothstep(0.22, 0.5, Math.abs(s - K.s) / gap)
      if (stops[i].global) weights.net = Math.max(weights.net, stops[i].act)
    }
    weights.hero = 1 - smoothstep(0.1, 0.5, (s - keys[0].s) / (keys[1].s - keys[0].s))
    return v
  }

  /** Whether the backlight sits away from its home side at this key: 1 when a place's copy is on the right, else 0. */
  function keyAway(K: Key): number {
    return keyShade(K) === 1 ? 1 : 0
  }

  /** Which side the shade falls on at this key: 1 = a place's copy on the right, -1 = on the left, 0 = no shade. */
  function keyShade(K: Key): number {
    return K.kind === 'place' ? (K.stop.copySide === 'right' ? 1 : -1) : 0
  }

  function keyView(vp: Viewport, K: Key, t: number): View {
    switch (K.kind) {
      case 'hero':
        return heroView(vp, t)
      case 'outro':
        return outroView(vp, t)
      case 'place':
        return placeView(vp, K.stop, t)
      case 'global':
        return globalView(vp, K.stop, t)
    }
  }

  /** Services: the whole globe, as large as at the hero and the global stop, in the middle. */
  function outroView(vp: Viewport, t: number): View {
    return {
      cx: vp.cx,
      cy: vp.cy,
      Rs: vp.mobile ? mobileRs(vp) : farRs(vp),
      d: FAR_D,
      lam: longitudeAt(t - T_DET),
      tilt: TILT,
    }
  }

  function globalView(vp: Viewport, s: CameraStop, t: number): View {
    const { width: W, height: H, mobile } = vp
    const globeLeft = s.copySide === 'right'
    return {
      cx: mobile ? vp.cx : globeLeft ? W * GLOBAL_X : W * (1 - GLOBAL_X),
      cy: mobile ? H * 0.62 : vp.cy,
      Rs: mobile ? Math.min(W * 0.44, H * 0.3) : farRs(vp),
      d: FAR_D,
      lam: longitudeAt(t - T_DET),
      tilt: TILT,
    }
  }

  function placeView(vp: Viewport, s: CameraStop, t: number): View {
    const { width: W, height: H, mobile } = vp
    const globeLeft = s.copySide === 'right'
    const Rs = mobile ? H * 0.75 : H * NEAR_R
    const d = NEAR_D
    const F = Rs * Math.sqrt(d * d - 1)
    const a = PLACE_ABOVE * RAD
    const up =
      (F * Math.sin(a)) / (d - Math.cos(a)) /* how far above the centre the place projects */
    const cyc = mobile ? H * 0.62 : H * PLACE_Y
    return {
      cx: mobile ? vp.cx : globeLeft ? W * SIDE_X : W * (1 - SIDE_X),
      cy: cyc + up,
      Rs,
      d,
      lam: s.lon * RAD + DRIFT * RAD * Math.sin(t * 0.21 + s.lon),
      tilt: (s.lat - PLACE_ABOVE) * RAD,
    }
  }

  function heroView(vp: Viewport, t: number): View {
    const { width: W, height: H } = vp
    const v: View = vp.mobile
      ? {
          cx: vp.cx,
          cy: H * 0.34,
          Rs: mobileRs(vp),
          d: FAR_D,
          lam: longitudeAt(t - T_DET),
          tilt: TILT,
        }
      : {
          cx: W * HERO_X,
          cy: vp.cy,
          Rs: farRs(vp),
          d: FAR_D,
          lam: longitudeAt(t - T_DET),
          tilt: TILT,
        }
    /* hovering a place turns the globe towards it (see hoverTurn) */
    v.lam += hTurn.x
    v.tilt += hTilt.x
    return v
  }

  /** Exactly where the intro leaves the globe. */
  function introView(vp: Viewport, t: number): View {
    const tau = t - T_DET
    return {
      cx: vp.cx,
      cy: vp.cy,
      Rs: radiusAt(tau) * shellAt(tau) * vp.min,
      d: FAR_D,
      lam: longitudeAt(tau),
      tilt: TILT,
    }
  }
}

export type Camera = ReturnType<typeof createCamera>
